import {
  useCallback,
  useState,
  useEffect,
  useEffect as useEffectExpose,
} from "react";
import toast from "react-hot-toast";
import {
  employeeService,
  employeeCodeStructureService,
  attendanceStructureService,
} from "../../../services/api";
import FloatingInput from "../FloatingInput.jsx";
import DatePicker from "../../Common/DatePicker.jsx";
import SearchableSelect from "../SearchableSelect.jsx";
import FloatingInputWithNA from "../FloatingInputWithNA.jsx";
import {
  PAN_SENTINEL_NOT_AVAILABLE,
  PAN_SENTINEL_APPLIED,
  canonicalizePanValue,
  isPanSentinelForUniqueness,
} from "../../../constants/panSentinels.js";

// Helper function to convert number to words (Indian currency format)
const numberToWords = (num) => {
  if (!num || num === 0) return "";
  const numValue = parseFloat(num);
  if (isNaN(numValue) || numValue < 0) return "";
  const integerPart = Math.floor(numValue);
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const convertHundreds = (n) => {
    let result = "";
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    }
    if (n > 0) {
      result += ones[n] + " ";
    }
    return result.trim();
  };
  if (integerPart === 0) return "";
  let result = "";
  let remaining = integerPart;
  if (remaining >= 10000000) {
    const crores = Math.floor(remaining / 10000000);
    result += convertHundreds(crores) + " Crore ";
    remaining %= 10000000;
  }
  if (remaining >= 100000) {
    const lakhs = Math.floor(remaining / 100000);
    result += convertHundreds(lakhs) + " Lakh ";
    remaining %= 100000;
  }
  if (remaining >= 1000) {
    const thousands = Math.floor(remaining / 1000);
    result += convertHundreds(thousands) + " Thousand ";
    remaining %= 1000;
  }
  if (remaining > 0) {
    result += convertHundreds(remaining);
  }
  return result.trim();
};

/** True when relieved date is set and is on or before today (start of local day). */
function isRelievedDateOnOrBeforeToday(relievedDateStr) {
  if (!relievedDateStr) return false;
  const d = new Date(relievedDateStr);
  if (Number.isNaN(d.getTime())) return false;
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() <= today.getTime();
}

function isRelievedDateAfterToday(relievedDateStr) {
  if (!relievedDateStr) return false;
  const d = new Date(relievedDateStr);
  if (Number.isNaN(d.getTime())) return false;
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() > today.getTime();
}

export function OrgDetailsForm({
  employeeData,
  setEmployeeData,
  onSaveSection,
  rejoinSelectedEmployeeId,
  loading,
  feedback,
  darkMode,
  onCancel,
  onNext,
  onPrevious,
  isFirstTab,
  isLastTab,
  readOnly = false,
  isEmployeeView = false,
  masterData = {},
  masterDataLoading = false,
  hideButtons = false,
  onSaveTrigger,
  onNavigateToTransfer,
  /** Called after employee org details persist successfully (including after modal "Continue" flows). Lets parent reset footer Edit vs Update. */
  onSaveSuccess,
}) {
  // Use employeeData for form values - this ensures formData is always current
  const formData = employeeData;
  const [formErrors, setFormErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [employeeCodeStructure, setEmployeeCodeStructure] = useState(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [showStatutoryWarningModal, setShowStatutoryWarningModal] = useState({
    show: false,
    field: null,
    message: '',
    title: '',
  });
  const [pendingCheckboxChange, setPendingCheckboxChange] = useState(null);
  
  // Master details tracking
  const [initialMasterDetails, setInitialMasterDetails] = useState({});
  const [snapshotEmployeeId, setSnapshotEmployeeId] = useState(null);
  const [masterDetailsChanged, setMasterDetailsChanged] = useState(false);
  const [showMasterDetailsWarning, setShowMasterDetailsWarning] = useState(false);
  const [showConfirmedToProbationWarning, setShowConfirmedToProbationWarning] =
    useState(false);
  const [showRelievedLockedWarning, setShowRelievedLockedWarning] =
    useState(false);
  const [showRejoinBeforeRelieveWarning, setShowRejoinBeforeRelieveWarning] =
    useState(false);
  const [confirmedMasterChange, setConfirmedMasterChange] = useState(false);
  const [showLockedAttendanceDateWarning, setShowLockedAttendanceDateWarning] =
    useState(false);
  const [lockedAttendanceWarningMonths, setLockedAttendanceWarningMonths] =
    useState([]);

  // Track initial CTC for change detection
  const [initialCTC, setInitialCTC] = useState(null);
  /** DB employment status when this employee record was loaded (edit mode only). */
  const [initialEmploymentStatus, setInitialEmploymentStatus] = useState(null);
  const [initialEmploymentDates, setInitialEmploymentDates] = useState({
    joiningDate: null,
    relievedDate: null,
  });
  const [initialIdentitySnapshot, setInitialIdentitySnapshot] = useState({
    officialEmail: "",
    personalEmail: "",
    pan: "",
    aadhaar: "",
    mobile: "",
  });

  const normalizeEmailForCompare = (value) => {
    const v = String(value || "").trim().toLowerCase();
    if (!v || v === "na" || v === "not available") return "";
    return v;
  };

  const normalizePanForCompare = (value) => {
    if (isPanSentinelForUniqueness(value)) return "";
    const v = canonicalizePanValue(value);
    if (isPanSentinelForUniqueness(v)) return "";
    return String(v || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "");
  };

  const normalizeAadhaarForCompare = (value) => {
    const v = String(value || "").replace(/\D/g, "");
    return v || "";
  };

  /** Last 10 digits for India; strips leading 91 when present. */
  const normalizeMobileForCompare = (value) => {
    let digits = String(value || "").replace(/\D/g, "");
    if (digits.length >= 12 && digits.startsWith("91")) {
      digits = digits.slice(-10);
    } else if (digits.length > 10) {
      digits = digits.slice(-10);
    }
    return digits.length === 10 ? digits : "";
  };

  const validateUniqueIdentityFields = async () => {
    const currentId = employeeData?.id != null ? String(employeeData.id) : null;
    const rejoinFromIdCandidates = [
      rejoinSelectedEmployeeId,
      employeeData?.rejoinFromEmployeeId,
      employeeData?.rejoinFromId,
      employeeData?.sourceEmployeeId,
      employeeData?.parentEmployeeId,
      employeeData?.previousEmployeeId,
    ]
      .filter((v) => v != null && v !== "")
      .map((v) => String(v));

    const targetOfficialEmail = normalizeEmailForCompare(formData.email);
    const targetPersonalEmail = normalizeEmailForCompare(formData.user?.email);
    const targetPan = normalizePanForCompare(formData.panNumber);
    const targetAadhaar = normalizeAadhaarForCompare(formData.aadharNumber);
    const targetMobile = normalizeMobileForCompare(formData.user?.phone);

    // Only when editing or explicit rejoin: infer historical source row IDs to ignore (API may omit rejoinFrom).
    // Pure "Add employee" without rejoin must NOT whitelist relieved/resigned/terminated rows — that was blocking real duplicates.
    const allowImplicitHistoricalRejoinMatch =
      Boolean(currentId) || rejoinFromIdCandidates.length > 0;

    const implicitRejoinSourceIds = new Set();

    if (allowImplicitHistoricalRejoinMatch) {
      const token =
        (formData.panNumber || "").trim() ||
        String(formData.aadharNumber || "").replace(/\D/g, "") ||
        String(formData.user?.phone || "").replace(/\D/g, "") ||
        targetOfficialEmail ||
        targetPersonalEmail ||
        "";
      if (token) {
        try {
          const r = await employeeService.getEmployees({
            page: 1,
            limit: 100,
            search: token,
          });
          const candidates = r?.employees || r?.data || [];
          for (const emp of candidates) {
            const ownerId = emp?.id ?? emp?.employeeId ?? emp?.userId;
            if (!ownerId) continue;
            if (currentId && String(ownerId) === currentId) continue;

            const status = String(emp?.employmentStatus || "")
              .trim()
              .toLowerCase();
            const isHistorical =
              emp?.editLocked === true ||
              status === "relieved" ||
              status === "resigned" ||
              status === "terminated";
            if (!isHistorical) continue;

            const empOfficialEmail = normalizeEmailForCompare(emp?.email);
            const empPersonalEmail = normalizeEmailForCompare(emp?.user?.email);
            const empPan = normalizePanForCompare(emp?.panNumber);
            const empAadhaar = normalizeAadhaarForCompare(emp?.aadharNumber);
            const empMobile = normalizeMobileForCompare(
              emp?.user?.phone ?? emp?.phone ?? emp?.mobileNumber
            );

            const identityMatched =
              (targetPan && targetPan === empPan) ||
              (targetAadhaar && targetAadhaar === empAadhaar) ||
              (targetMobile && targetMobile === empMobile) ||
              (targetOfficialEmail &&
                (targetOfficialEmail === empOfficialEmail ||
                  targetOfficialEmail === empPersonalEmail)) ||
              (targetPersonalEmail &&
                (targetPersonalEmail === empOfficialEmail ||
                  targetPersonalEmail === empPersonalEmail));

            if (identityMatched) {
              implicitRejoinSourceIds.add(String(ownerId));
            }
          }
        } catch (e) {
          console.warn("Implicit rejoin candidate fetch:", e);
        }
      }
    }

    const ignoreEmployeeIds = [
      ...new Set([
        ...rejoinFromIdCandidates,
        ...Array.from(implicitRejoinSourceIds),
        ...(currentId ? [currentId] : []),
      ]),
    ]
      .filter(Boolean)
      .join(",");

    const dupRes = await employeeService.checkEmployeeIdentityDuplicates({
      officialEmail: formData.email || "",
      personalEmail: formData.user?.email || "",
      pan: formData.panNumber || "",
      aadhar: formData.aadharNumber || "",
      mobile: formData.user?.phone || "",
      ignoreEmployeeIds,
    });

    const conflicts = dupRes?.conflicts || [];
    if (conflicts.length === 0) return;

    const duplicateErrors = {};
    const seen = new Set();
    for (const c of conflicts) {
      for (const m of c.matches || []) {
        if (seen.has(m)) continue;
        seen.add(m);
        if (m === "officialEmail") {
          duplicateErrors.email =
            "Official email already exists for another employee. Use a unique email.";
        } else if (m === "personalEmail") {
          duplicateErrors.personalEmail =
            "Personal email already exists for another employee. Use a unique email.";
        } else if (m === "pan") {
          duplicateErrors.panNumber =
            "PAN already exists for another employee. Use a unique PAN.";
        } else if (m === "aadhar") {
          duplicateErrors.aadharNumber =
            "Aadhaar already exists for another employee. Use a unique Aadhaar.";
        } else if (m === "mobile") {
          duplicateErrors.mobileNumber =
            "Mobile number already exists for another employee. Use a unique mobile number.";
        }
      }
    }

    if (Object.keys(duplicateErrors).length > 0) {
      setFormErrors((prev) => ({ ...prev, ...duplicateErrors }));
      setTouchedFields((prev) => ({
        ...prev,
        ...(duplicateErrors.email ? { email: true } : {}),
        ...(duplicateErrors.personalEmail ? { personalEmail: true } : {}),
        ...(duplicateErrors.panNumber ? { panNumber: true } : {}),
        ...(duplicateErrors.aadharNumber ? { aadharNumber: true } : {}),
        ...(duplicateErrors.mobileNumber ? { mobileNumber: true } : {}),
      }));
      throw new Error(
        "Duplicate email/mobile/PAN/Aadhaar found. These values must be unique unless rejoining from the same employee."
      );
    }
  };

  // Use totalEmployees from masterData instead of local state
  const totalEmployees = masterData.totalEmployees || 0;

  // Initialize statutory compliance fields with default values if they don't exist
  useEffect(() => {
    if (
      employeeData &&
      (!Object.prototype.hasOwnProperty.call(employeeData, "pfApplicable") ||
        !Object.prototype.hasOwnProperty.call(employeeData, "ceilingPf") ||
        !Object.prototype.hasOwnProperty.call(employeeData, "epsApplicable") ||
        !Object.prototype.hasOwnProperty.call(employeeData, "esiApplicable") ||
        !Object.prototype.hasOwnProperty.call(employeeData, "ptApplicable") ||
        !Object.prototype.hasOwnProperty.call(
          employeeData,
          "gratuityApplicable"
        ))
    ) {
      setEmployeeData((prev) => ({
        ...prev,
        pfApplicable: prev.pfApplicable !== false, // Default to true
        ceilingPf: prev.ceilingPf !== false, // Default to true
        epsApplicable: prev.epsApplicable || false, // Default to false
        esiApplicable: prev.esiApplicable || false, // Default to false
        ptApplicable: prev.ptApplicable !== false, // Default to true
        gratuityApplicable: prev.gratuityApplicable !== false, // Default to true
      }));
    }
  }, [employeeData, setEmployeeData]);

  // Map legacy PAN strings from API ("Not Available", "PAN Applied", etc.) to canonical tokens for display
  useEffect(() => {
    if (!employeeData?.id || employeeData.panNumber == null) return;
    const raw = String(employeeData.panNumber).trim();
    if (!raw) return;
    const canon = canonicalizePanValue(raw);
    if (canon !== raw) {
      setEmployeeData((prev) =>
        prev && prev.id === employeeData.id ? { ...prev, panNumber: canon } : prev
      );
    }
  }, [employeeData?.id, employeeData?.panNumber, setEmployeeData]);

  // Initialize employment status with default value if not set
  useEffect(() => {
    if (employeeData && !employeeData.employmentStatus) {
      setEmployeeData((prev) => ({
        ...prev,
        employmentStatus: "Probation",
      }));
    }
  }, [employeeData, setEmployeeData]);

  // Initialize Send Invite only if not set (do not override user selection / defaults)
  useEffect(() => {
    if (!employeeData) return;
    setEmployeeData((prev) => {
      if (!prev) return prev;
      if (prev.inviteSent !== undefined && prev.inviteSent !== null) return prev;
      return { ...prev, inviteSent: false };
    });
  }, [employeeData?.id, setEmployeeData]);

  // Initialize snapshot of master details ONCE per employee id
  useEffect(() => {
    if (employeeData && employeeData.id) {
      if (snapshotEmployeeId !== employeeData.id) {
        const masterFields = {
          branchId: employeeData.branchId,
          designationId: employeeData.designationId,
          subDepartmentId: employeeData.subDepartmentId,
          departmentId: employeeData.departmentId,
          gradeId: employeeData.gradeId,
          categoryId: employeeData.categoryId,
          reportingManagerId: employeeData.reportingManagerId,
        };
        setInitialMasterDetails(masterFields);
        setInitialCTC(employeeData.ctc || null); // Track initial CTC
        setInitialEmploymentStatus(employeeData.employmentStatus ?? null);
        setInitialEmploymentDates({
          joiningDate: employeeData.joiningDate || null,
          relievedDate: employeeData.relievedDate || null,
        });
        setInitialIdentitySnapshot({
          officialEmail: normalizeEmailForCompare(employeeData.email),
          personalEmail: normalizeEmailForCompare(employeeData.user?.email),
          pan: normalizePanForCompare(employeeData.panNumber),
          aadhaar: normalizeAadhaarForCompare(employeeData.aadharNumber),
          mobile: normalizeMobileForCompare(employeeData.user?.phone),
        });
        setSnapshotEmployeeId(employeeData.id);
      }
    } else {
      // For new employee, clear snapshot
      if (snapshotEmployeeId !== null) {
        setSnapshotEmployeeId(null);
        setInitialMasterDetails({});
        setInitialCTC(null);
        setInitialEmploymentStatus(null);
        setInitialEmploymentDates({ joiningDate: null, relievedDate: null });
        setInitialIdentitySnapshot({
          officialEmail: "",
          personalEmail: "",
          pan: "",
          aadhaar: "",
          mobile: "",
        });
      }
    }
  }, [employeeData?.id]);

  // Check for master details changes
  useEffect(() => {
    if (employeeData && employeeData.id && Object.keys(initialMasterDetails).length > 0) {
      const currentMasterFields = {
        branchId: employeeData.branchId,
        designationId: employeeData.designationId,
        subDepartmentId: employeeData.subDepartmentId,
        departmentId: employeeData.departmentId,
        gradeId: employeeData.gradeId,
        categoryId: employeeData.categoryId,
        reportingManagerId: employeeData.reportingManagerId,
      };

      const hasChanges = Object.keys(currentMasterFields).some(
        (key) => currentMasterFields[key] !== initialMasterDetails[key]
      );
      
      setMasterDetailsChanged(hasChanges);
    }
  }, [employeeData.branchId, employeeData.designationId, employeeData.subDepartmentId, employeeData.departmentId, employeeData.gradeId, employeeData.categoryId, employeeData.reportingManagerId, initialMasterDetails]);

  // Memoize the reporting employees fetch function to prevent unnecessary API calls
  const fetchReportingEmployees = useCallback(() => {
    return employeeService.getReportingEmployees(employeeData.id);
  }, [employeeData.id]);

  // Fetch employee code structure
  const fetchEmployeeCodeStructure = useCallback(async () => {
    try {
      const data = await employeeCodeStructureService.getCurrentStructure();
      setEmployeeCodeStructure(data.data);
      return data.data;
    } catch (error) {
      console.error("Error fetching employee code structure:", error);
    }
    return null;
  }, []);

  // Generate automated employee code
  const generateAutomatedEmployeeCode = useCallback(async () => {
    if (
      !employeeCodeStructure ||
      employeeCodeStructure.patternType !== "automated"
    ) {
      return;
    }

    setIsGeneratingCode(true);
    try {
      const data = await employeeCodeStructureService.getNextEmployeeCode();
      const nextCode = data.data.nextCode;

      setEmployeeData((prev) => ({
        ...prev,
        empCode: nextCode,
      }));
    } catch (error) {
      console.error("Error generating employee code:", error);
      toast.error(error.message || "Failed to generate employee code");
    } finally {
      setIsGeneratingCode(false);
    }
  }, [employeeCodeStructure, setEmployeeData]);

  // Fetch employee code structure on component mount
  useEffect(() => {
    if (!isEmployeeView) {
      // Only for admin users, not employee view
      fetchEmployeeCodeStructure();
    }
  }, [fetchEmployeeCodeStructure, isEmployeeView]);

  // Generate automated employee code when structure is loaded and code is empty
  // for new/rejoin flows. Rejoin prefill explicitly clears empCode, so this
  // must rerun even when employeeData.id state didn't change.
  useEffect(() => {
    const currentCode = String(employeeData?.empCode || "").trim();
    if (
      employeeCodeStructure &&
      employeeCodeStructure.patternType === "automated" &&
      !isEmployeeView &&
      !employeeData?.id && // Only for create/rejoin forms
      !currentCode // Re-generate when code is currently empty
    ) {
      // Generate code for new employee/rejoin when code field is empty.
      generateAutomatedEmployeeCode();
    }
  }, [
    employeeCodeStructure,
    isEmployeeView,
    employeeData?.id,
    employeeData?.empCode,
    rejoinSelectedEmployeeId,
    generateAutomatedEmployeeCode,
  ]);

  // Validation functions
  const validateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "Date of Birth is required";

    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    if (age < 18) {
      return "Employee must be at least 18 years old";
    }
    if (age > 100) {
      return "Employee age cannot be more than 100 years";
    }
    return null;
  };

  const validateEmail = (email) => {
    const raw = email || "";
    const trimmed = raw.trim();
    const lowered = trimmed.toLowerCase();

    // Allow special sentinel values for missing email
    if (lowered === "na" || lowered === "not available") {
      return null;
    }

    if (!trimmed) return "Email is required";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      return "Please enter a valid email address";
    }
    return null;
  };

  const validateMobileNumber = (phone) => {
    if (!phone) return "Mobile number is required";
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(phone)) {
      return "Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9";
    }
    return null;
  };

  const validatePAN = (pan) => {
    const raw = pan || "";
    const trimmed = raw.trim();
    const canon = canonicalizePanValue(trimmed);

    if (isPanSentinelForUniqueness(canon)) return null;

    if (!trimmed) return "PAN is required";

    const upper = trimmed.toUpperCase().replace(/\s+/g, "");
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(upper)) {
      return "Please enter a valid PAN number (e.g., ABCDE1234F)";
    }
    // Check for common invalid patterns
    const invalidPatterns = [
      "AAAAA0000A",
      "BBBBB0000B",
      "CCCCC0000C",
      "DDDDD0000D",
      "EEEEE0000E",
      "FFFFF0000F",
      "GGGGG0000G",
      "HHHHH0000H",
      "IIIII0000I",
      "JJJJJ0000J",
      "KKKKK0000K",
      "LLLLL0000L",
      "MMMMM0000M",
      "NNNNN0000N",
      "OOOOO0000O",
      "PPPPP0000P",
      "QQQQQ0000Q",
      "RRRRR0000R",
      "SSSSS0000S",
      "TTTTT0000T",
      "UUUUU0000U",
      "VVVVV0000V",
      "WWWWW0000W",
      "XXXXX0000X",
      "YYYYY0000Y",
      "ZZZZZ0000Z",
    ];
    if (invalidPatterns.includes(upper)) {
      return "Please enter a valid PAN number";
    }
    return null;
  };

  const validateAadhaar = (aadhaar) => {
    if (!aadhaar) return "Aadhaar number is required";
    const aadhaarRegex = /^[0-9]{12}$/;
    if (!aadhaarRegex.test(aadhaar)) {
      return "Please enter a valid 12-digit Aadhaar number";
    }
    // Check for common invalid patterns
    if (
      aadhaar === "000000000000" ||
      aadhaar === "111111111111" ||
      aadhaar === "999999999999"
    ) {
      return "Please enter a valid Aadhaar number";
    }
    return null;
  };

  const validateEmployeeCode = (empCode) => {
    if (!empCode) return "Employee Code is required";
    if (empCode.length < 1) {
      return "Employee Code must be at least 1 character long";
    }
    if (empCode.length > 20) {
      return "Employee Code cannot exceed 20 characters";
    }
    // Check if employee code contains only alphanumeric characters, hyphens, forward slashes, and dots
    const allowedRegex = /^[a-zA-Z0-9\-/.]+$/;
    if (!allowedRegex.test(empCode)) {
      return "Employee Code can only contain letters, numbers, hyphens (-), forward slashes (/), and dots (.)";
    }
    return null;
  };

  const normalizeNameInput = (name) => {
    if (typeof name !== "string") return "";
    // Trim + collapse extra spaces (keeps only single spaces between words)
    return name.replace(/\s+/g, " ").trim();
  };

  // Title case for display while typing: "kranthi dev sai" -> "Kranthi Dev Sai"
  const toTitleCaseName = (name) => {
    if (typeof name !== "string") return "";
    const collapsed = name.replace(/\s+/g, " ");
    return collapsed
      .split(" ")
      .map((word) =>
        word.length
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          : ""
      )
      .join(" ")
      .replace(/\s+/g, " "); // collapse again after split (handles leading/trailing)
  };

  const toCamelCaseName = (name) => {
    const normalized = normalizeNameInput(name);
    if (!normalized) return "";

    const words = normalized.split(" ").filter(Boolean);
    if (words.length === 0) return "";

    const first = words[0].toLowerCase();
    const rest = words
      .slice(1)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

    return [first, ...rest].join("");
  };

  // Returns camelCase name only if existing validation passes; otherwise returns null.
  const getCamelCaseNameIfValid = (name) => {
    const error = validateName(name);
    if (error) return null;
    return toCamelCaseName(name);
  };

  const validateName = (name) => {
    const normalized = normalizeNameInput(name);
    if (!normalized) return "Name is required";
    if (normalized.length < 2) {
      return "Name must be at least 2 characters long";
    }
    if (normalized.length > 50) {
      return "Name cannot exceed 50 characters";
    }
    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!nameRegex.test(normalized)) {
      return "Name can only contain letters and spaces";
    }
    return null;
  };

  const validateJoiningDate = (joiningDate, dateOfBirth) => {
    if (!joiningDate) return "Joining Date is required";

    const joinDate = new Date(joiningDate);
    const today = new Date();

    // Joining date should not be in the future
    if (joinDate > today) {
      return "Joining Date cannot be in the future";
    }

    if (dateOfBirth) {
      const birthDate = new Date(dateOfBirth);
      let ageAtJoining = joinDate.getFullYear() - birthDate.getFullYear();
      const monthDiff = joinDate.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && joinDate.getDate() < birthDate.getDate())
      ) {
        ageAtJoining--;
      }

      if (ageAtJoining < 18) {
        return "Employee must be at least 18 years old at joining date";
      }
    }

    return null;
  };

  const validateConfirmationDate = (confirmationDate, joiningDate) => {
    if (!confirmationDate) return "Confirmation Date is required";

    const confirmDate = new Date(confirmationDate);
    const joinDate = new Date(joiningDate);
    const today = new Date();

    if (confirmDate < joinDate) {
      return "Confirmation Date cannot be before Joining Date";
    }

    // if (confirmDate > today) {
    //     return 'Confirmation Date cannot be in the future';
    // }

    return null;
  };

  const validateResignationDate = (resignationDate, joiningDate) => {
    if (!resignationDate) return "Resignation Date is required";

    const resignDate = new Date(resignationDate);
    const joinDate = new Date(joiningDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    resignDate.setHours(0, 0, 0, 0);

    if (resignDate < joinDate) {
      return "Resignation Date cannot be before Joining Date";
    }

    // ✅ NEW: Check if resignation date is in the past (backdated)
    // if (resignDate < today) {
    //   return "Resignation Date cannot be in the past. Backdated resignations are not allowed.";
    // }

    return null;
  };

  const validateRelievedDate = (relievedDate, resignationDate, joiningDate) => {
    if (!relievedDate) return "Relieved Date is required";

    const relieveDate = new Date(relievedDate);
    const joinDate = new Date(joiningDate);
    const today = new Date();

    if (relieveDate < joinDate) {
      return "Relieved Date cannot be before Joining Date";
    }

    if (resignationDate) {
      const resignDate = new Date(resignationDate);
      if (relieveDate < resignDate) {
        return "Relieved Date cannot be before Resignation Date";
      }
    }

    // if (relieveDate > today) {
    //     return 'Relieved Date cannot be in the future';
    // }

    return null;
  };

  const validateCTC = (ctc) => {
    if (!ctc) return "CTC is required";
    if (isNaN(ctc) || parseFloat(ctc) <= 0) {
      return "CTC must be a valid positive number";
    }
    if (parseFloat(ctc) > 999999999) {
      return "CTC cannot exceed 999,999,999";
    }
    return null;
  };

  // Validate individual field
  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case "empCode":
        return validateEmployeeCode(value);
      case "name":
        return validateName(value);
      case "dateOfBirth":
        return validateAge(value);
      case "email":
        return validateEmail(value);
      case "personalEmail":
        return validateEmail(value);
      case "mobileNumber":
        return validateMobileNumber(value);
      case "panNumber":
        return validatePAN(value);
      case "aadharNumber":
        return validateAadhaar(value);
      case "bloodGroup":
        return !value ? "Blood Group is required" : null;
      case "joiningDate":
        return validateJoiningDate(value, formData.user?.dateOfBirth);
      case "confirmationDate":
        return validateConfirmationDate(value, formData.joiningDate);
      case "resignationDate":
        return validateResignationDate(value, formData.joiningDate);
      case "relievedDate":
        return validateRelievedDate(
          value,
          formData.resignationDate,
          formData.joiningDate
        );
      case "ctc":
        return validateCTC(value);
      case "branchId":
      case "designationId":
      case "departmentId":
      case "subDepartmentId":
      case "gradeId":
      case "categoryId":
      case "employmentType":
      case "employmentStatus":
      case "gender":
        return !value
          ? `${
              fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
            } is required`
          : null;
      default:
        return null;
    }
  };

  // Comprehensive validation function for entire form
  const validateForm = () => {
    const errors = {};

    // Basic validations
    errors.empCode = validateEmployeeCode(formData.empCode);
    errors.name = validateName(formData.user?.name);
    errors.dateOfBirth = validateAge(formData.user?.dateOfBirth);
    errors.email = validateEmail(formData.email);
    errors.personalEmail = validateEmail(formData.user?.email);
    errors.mobileNumber = validateMobileNumber(formData.user?.phone);
    errors.panNumber = validatePAN(formData.panNumber);
    errors.aadharNumber = validateAadhaar(formData.aadharNumber);
    errors.ctc = validateCTC(formData.ctc);
    errors.joiningDate = validateJoiningDate(
      formData.joiningDate,
      formData.user?.dateOfBirth
    );

    // Conditional validations
    if (formData.employmentStatus === "Confirmed") {
      if (!formData.confirmationDate) {
        errors.confirmationDate =
          "Confirmation Date is required for confirmed employees";
      } else {
        errors.confirmationDate = validateConfirmationDate(
          formData.confirmationDate,
          formData.joiningDate
        );
      }
    }

    if (
      formData.employmentStatus === "Resigned" ||
      formData.employmentStatus === "Relieved"
    ) {
      if (!formData.resignationDate) {
        errors.resignationDate =
          "Resignation Date is required for resigned/relieved employees";
      } else {
        errors.resignationDate = validateResignationDate(
          formData.resignationDate,
          formData.joiningDate
        );
      }

      if (!formData.relievedDate) {
        errors.relievedDate =
          "Relieved Date is required for resigned/relieved employees";
      } else {
        errors.relievedDate = validateRelievedDate(
          formData.relievedDate,
          formData.resignationDate,
          formData.joiningDate
        );
      }

      if (!formData.reason) {
        errors.reason =
          "Resignation Reason is required for resigned/relieved employees";
      }
    }

    if (formData.employmentStatus === "Terminated") {
      if (!formData.terminationDate) {
        errors.terminationDate =
          "Termination Date is required for terminated employees";
      } else {
        errors.terminationDate = validateRelievedDate(
          formData.terminationDate,
          formData.resignationDate,
          formData.joiningDate
        );
      }

      if (!formData.reason) {
        errors.reason =
          "Termination Reason is required for terminated employees";
      }
    }

    // Required field validations
    if (!formData.branchId) errors.branchId = "Branch is required";
    if (!formData.designationId)
      errors.designationId = "Designation is required";
    if (!formData.departmentId) errors.departmentId = "Department is required";
    if (!formData.subDepartmentId)
      errors.subDepartmentId = "Sub Department is required";
    if (!formData.gradeId) errors.gradeId = "Grade is required";
    if (!formData.categoryId) errors.categoryId = "Category is required";
    if (!formData.employmentType)
      errors.employmentType = "Employment Type is required";

    if (!formData.employmentStatus) {
      errors.employmentStatus = "Employment Status is required";
    }

    if (!formData.user?.gender) errors.gender = "Gender is required";
    if (!formData.user?.bloodGroup)
      errors.bloodGroup = "Blood Group is required";

    // Only require reporting manager if there are existing employees
    // Make it optional for admin users
    const userRole = formData.user?.role;
    const isAdmin = userRole === "admin" || userRole === "super_admin";
    if (totalEmployees > 0 && !formData.reportingManagerId && !isAdmin) {
      errors.reportingManagerId = "Reporting Manager is required";
    }

    // Additional validation for employment status specific fields
    if (
      formData.employmentStatus === "Confirmed" &&
      !formData.confirmationDate
    ) {
      errors.confirmationDate =
        "Confirmation Date is required for confirmed employees";
    }

    if (
      formData.employmentStatus === "Resigned" ||
      formData.employmentStatus === "Relieved"
    ) {
      if (!formData.resignationDate) {
        errors.resignationDate =
          "Resignation Date is required for resigned/relieved employees";
      }
      if (!formData.relievedDate) {
        errors.relievedDate =
          "Relieved Date is required for resigned/relieved employees";
      }
      if (!formData.reason) {
        errors.reason =
          "Resignation Reason is required for resigned/relieved employees";
      }
    }

    if (formData.employmentStatus === "Terminated") {
      if (!formData.terminationDate) {
        errors.terminationDate =
          "Termination Date is required for terminated employees";
      }
      if (!formData.reason) {
        errors.reason =
          "Termination Reason is required for terminated employees";
      }
    }

    // Remove null values
    Object.keys(errors).forEach((key) => {
      if (errors[key] === null) {
        delete errors[key];
      }
    });

    // Set the errors state and return a boolean indicating form validity.
    setFormErrors(errors);
    setIsFormValid(Object.keys(errors).length === 0);

    return Object.keys(errors).length === 0;
  };

  // Handle field blur - validate individual field when user leaves the field
  const handleFieldBlur = (fieldName, value) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));

    const error = validateField(fieldName, value);

    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  // Handle field change - validate and remove errors when value becomes valid
  const handleFieldChange = (fieldName, value) => {
    // Validate on change if field has been touched
    if (touchedFields[fieldName]) {
      const error = validateField(fieldName, value);
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: error,
      }));
    }
  };

  // Handle field change with immediate validation (for real-time feedback)
  const handleFieldChangeWithValidation = (fieldName, value) => {
    // Mark field as touched and validate immediately
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));

    const error = validateField(fieldName, value);

    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));

    // If dateOfBirth changes, re-validate joiningDate
    if (fieldName === "dateOfBirth" && formData.joiningDate) {
      const joiningDateError = validateJoiningDate(formData.joiningDate, value);
      setFormErrors((prev) => ({
        ...prev,
        joiningDate: joiningDateError,
      }));
      setTouchedFields((prev) => ({ ...prev, joiningDate: true }));
    }
  };

  // Handle employment status change and clear related fields
  const handleEmploymentStatusChange = (newStatus) => {
    if (
      formData?.id &&
      initialEmploymentStatus === "Confirmed" &&
      newStatus === "Probation"
    ) {
      setShowConfirmedToProbationWarning(true);
      return;
    }
    if (
      formData?.id &&
      initialEmploymentStatus === "Relieved" &&
      formData?.employmentStatus === "Relieved" &&
      newStatus !== "Relieved" &&
      isRelievedDateOnOrBeforeToday(initialEmploymentDates.relievedDate)
    ) {
      setShowRelievedLockedWarning(true);
      return;
    }
    setEmployeeData((prev) => {
      const updatedData = { ...prev, employmentStatus: newStatus };

      // Clear related fields based on status change
      if (newStatus !== "Confirmed") {
        updatedData.confirmationDate = null;
      }

      if (newStatus !== "Resigned" && newStatus !== "Relieved") {
        updatedData.resignationDate = null;
      }

      // Clear relievedDate when status is Terminated (use terminationDate instead)
      if (newStatus === "Terminated") {
        updatedData.relievedDate = null;
      } else if (
        newStatus !== "Resigned" &&
        newStatus !== "Relieved" &&
        newStatus !== "Terminated"
      ) {
        updatedData.relievedDate = null;
      }

      // Clear terminationDate when status is not Terminated
      if (newStatus !== "Terminated") {
        updatedData.terminationDate = null;
      }

      if (
        newStatus !== "Resigned" &&
        newStatus !== "Relieved" &&
        newStatus !== "Terminated"
      ) {
        updatedData.reason = null;
      }

      return updatedData;
    });

    // Clear form errors for cleared fields
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (newStatus !== "Confirmed") {
        delete newErrors.confirmationDate;
      }
      if (newStatus !== "Resigned" && newStatus !== "Relieved") {
        delete newErrors.resignationDate;
      }
      // Clear relievedDate error when status is Terminated (use terminationDate instead)
      if (newStatus === "Terminated") {
        delete newErrors.relievedDate;
      } else if (
        newStatus !== "Resigned" &&
        newStatus !== "Relieved" &&
        newStatus !== "Terminated"
      ) {
        delete newErrors.relievedDate;
      }
      
      // Clear terminationDate error when status is not Terminated
      if (newStatus !== "Terminated") {
        delete newErrors.terminationDate;
      }
      
      if (
        newStatus !== "Resigned" &&
        newStatus !== "Relieved" &&
        newStatus !== "Terminated"
      ) {
        delete newErrors.reason;
      }
      return newErrors;
    });

    // Only validate fields that are now required based on the new status
    // Don't validate fields that are not yet filled to avoid showing premature errors
    setTimeout(() => {
      if (newStatus === "Confirmed" && formData.confirmationDate) {
        handleFieldChangeWithValidation(
          "confirmationDate",
          formData.confirmationDate
        );
      }
      if (newStatus === "Resigned" || newStatus === "Relieved") {
        if (formData.resignationDate) {
          handleFieldChangeWithValidation(
            "resignationDate",
            formData.resignationDate
          );
        }
        if (formData.relievedDate) {
          handleFieldChangeWithValidation(
            "relievedDate",
            formData.relievedDate
          );
        }
        if (formData.reason) {
          handleFieldChangeWithValidation("reason", formData.reason);
        }
      }
      if (newStatus === "Terminated") {
        if (formData.terminationDate) {
          handleFieldChangeWithValidation(
            "terminationDate",
            formData.terminationDate
          );
        }
        if (formData.reason) {
          handleFieldChangeWithValidation("reason", formData.reason);
        }
      }

      // Don't validate the entire form here - only validate the specific fields that changed
      // This prevents showing errors for fields that haven't been filled yet
    }, 100);
  };

  const handleInputChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;
      let fieldValue = type === "checkbox" ? checked : value;

      // Special handling for employment status
      if (name === "employmentStatus") {
        handleEmploymentStatusChange(value);
        return;
      }

      // Auto-capitalize Employee Code
      if (name === "empCode") {
        fieldValue = fieldValue.toUpperCase();
      }

      // Auto-capitalize PAN Number (backend expects uppercase: ABCDE1234F); keep canonical sentinels
      if (name === "panNumber" && typeof fieldValue === "string") {
        const canon = canonicalizePanValue(fieldValue);
        if (!isPanSentinelForUniqueness(canon)) {
          fieldValue = fieldValue.replace(/\s/g, "").toUpperCase();
        } else {
          fieldValue = canon;
        }
      }

      // Aadhaar typing guard: numbers only, max 12 digits.
      if (name === "aadharNumber" && typeof fieldValue === "string") {
        fieldValue = fieldValue.replace(/\D/g, "").slice(0, 12);
      }

      setEmployeeData((prev) => ({
        ...prev,
        [name]: fieldValue,
      }));
      // Validate field immediately for real-time feedback
      handleFieldChangeWithValidation(name, fieldValue);
    },
    [setEmployeeData]
  );

  const handleSave = async (options = {}) => {
    const skipWarning = options.skipWarning === true;
    const forceConfirmed = options.forceConfirmed === true;
    if (readOnly && !isEmployeeView) {
      return { saved: false };
    }
    // For employee view, just navigate to next tab without any API calls
    if (isEmployeeView && onNext && !isLastTab) {
      onNext();
      return;
    }

    if (
      formData?.id &&
      initialEmploymentStatus === "Confirmed" &&
      formData.employmentStatus === "Probation"
    ) {
      setShowConfirmedToProbationWarning(true);
      return { saved: false };
    }

    // Validate entire form when save is clicked (only for admin users)
    const isValid = validateForm();

    if (!isValid) {
      toast.error("Please fix the validation errors before saving");
      // Scroll to the first error
      const firstErrorField = Object.keys(formErrors)[0];
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus();
        }
      }
      // Throw an error to indicate validation failure
      throw new Error("Validation failed");
    }

    // Check if master details have changed and show warning (only once until confirmed)
    const masterFields = [
      "branchId",
      "designationId",
      "subDepartmentId",
      "departmentId",
      "gradeId",
      "categoryId",
      "reportingManagerId"
    ];

    const hasMasterChanges =
      employeeData?.id &&
      Object.keys(initialMasterDetails).length > 0 &&
      masterFields.some(f => employeeData[f] !== initialMasterDetails[f]);

    if (!skipWarning && hasMasterChanges && !confirmedMasterChange) {

      setShowMasterDetailsWarning(true);
      
      return { warning: "MASTER_DETAILS_WARNING" };
    }

    const parseDateSafe = (value) => {
      if (!value) return null;
      const dt = new Date(value);
      if (Number.isNaN(dt.getTime())) return null;
      dt.setHours(0, 0, 0, 0);
      return dt;
    };

    const toMonthKey = (dateValue) =>
      dateValue.getFullYear() * 12 + (dateValue.getMonth() + 1);

    const getMonthLabel = (month, year) =>
      new Date(Number(year), Number(month) - 1, 1).toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      });

    const oldJoiningDate = parseDateSafe(initialEmploymentDates.joiningDate);
    const oldRelievedDate = parseDateSafe(initialEmploymentDates.relievedDate);
    const newJoiningDate = parseDateSafe(formData.joiningDate);
    const newRelievedDate = parseDateSafe(formData.relievedDate);

    const joiningChanged =
      (oldJoiningDate && newJoiningDate
        ? oldJoiningDate.getTime() !== newJoiningDate.getTime()
        : oldJoiningDate !== newJoiningDate);
    const relievedChanged =
      (oldRelievedDate && newRelievedDate
        ? oldRelievedDate.getTime() !== newRelievedDate.getTime()
        : oldRelievedDate !== newRelievedDate);
    const dateWindowChanged = Boolean(employeeData?.id) && (joiningChanged || relievedChanged);

    let lockedMonthsInRange = [];
    if (dateWindowChanged) {
      try {
        const locksRes = await attendanceStructureService.getAttendanceLocks();
        const lockRows = Array.isArray(locksRes?.data) ? locksRes.data : [];

        const oldStartKey = oldJoiningDate ? toMonthKey(oldJoiningDate) : null;
        const oldEndKey = oldRelievedDate ? toMonthKey(oldRelievedDate) : null;
        const newStartKey = newJoiningDate ? toMonthKey(newJoiningDate) : null;
        const newEndKey = newRelievedDate ? toMonthKey(newRelievedDate) : null;

        const inWindow = (lockKey, startKey, endKey) => {
          if (startKey == null) return false;
          if (lockKey < startKey) return false;
          if (endKey != null && lockKey > endKey) return false;
          return true;
        };

        // Only show locked months whose membership changed between old and new date windows.
        lockedMonthsInRange = lockRows
          .filter((lock) => {
            const m = Number(lock.month);
            const y = Number(lock.year);
            if (!m || !y) return false;
            const lockKey = y * 12 + m;
            const inOld = inWindow(lockKey, oldStartKey, oldEndKey);
            const inNew = inWindow(lockKey, newStartKey, newEndKey);
            const changedByRangeMembership = inOld !== inNew;

            // Even when a boundary month is present in both windows, day-level edits
            // can change attendance in that month (e.g., relieving date moved within Feb).
            const impactedOldRelievingMonth =
              relievedChanged && oldEndKey != null && lockKey === oldEndKey;
            const impactedNewRelievingMonth =
              relievedChanged && newEndKey != null && lockKey === newEndKey;
            const impactedOldJoiningMonth =
              joiningChanged && oldStartKey != null && lockKey === oldStartKey;
            const impactedNewJoiningMonth =
              joiningChanged && newStartKey != null && lockKey === newStartKey;

            return (
              changedByRangeMembership ||
              impactedOldRelievingMonth ||
              impactedNewRelievingMonth ||
              impactedOldJoiningMonth ||
              impactedNewJoiningMonth
            );
          })
          .map((lock) => ({
            month: Number(lock.month),
            year: Number(lock.year),
            label: getMonthLabel(lock.month, lock.year),
          }))
          .sort((a, b) => (a.year - b.year) || (a.month - b.month));
      } catch (e) {
        console.warn("Failed to fetch attendance locks for DOJ/DOL validation:", e);
      }

      if (lockedMonthsInRange.length > 0) {
        setLockedAttendanceWarningMonths(lockedMonthsInRange);
        setShowLockedAttendanceDateWarning(true);
        const labels = lockedMonthsInRange.map((m) => m.label).join(", ");
        toast.error(
          `Attendance is locked for: ${labels}. Unlock those month(s) in Attendance, then update Date of Join or Relieving Date.`,
          { duration: 9000 }
        );
        return { saved: false };
      }
    }


    // Prevent duplicate identity data across employees.
    // Performance: skip duplicate-check API calls if identity fields are unchanged in edit mode.
    const currentIdentitySnapshot = {
      officialEmail: normalizeEmailForCompare(formData.email),
      personalEmail: normalizeEmailForCompare(formData.user?.email),
      pan: normalizePanForCompare(formData.panNumber),
      aadhaar: normalizeAadhaarForCompare(formData.aadharNumber),
      mobile: normalizeMobileForCompare(formData.user?.phone),
    };
    const identityChanged = Object.keys(currentIdentitySnapshot).some(
      (k) => currentIdentitySnapshot[k] !== initialIdentitySnapshot[k]
    );
    if (!employeeData?.id || identityChanged) {
      // Rejoin flow can only reuse values from the selected source employee.
      await validateUniqueIdentityFields();
    }

    // Prepare data matching backend API expectations
    const nameForSave = normalizeNameInput(formData.user?.name ?? "") || formData.user?.name;
    const isRejoin = Boolean(rejoinSelectedEmployeeId);
    if (isRejoin && isRelievedDateAfterToday(formData?.rejoinSourceRelievedDate)) {
      setShowRejoinBeforeRelieveWarning(true);
      return { saved: false };
    }

    const panForSave = canonicalizePanValue(formData.panNumber || "");

    const sectionData = {
      // Rejoin: backend creates new user + new employee and locks old record
      ...(isRejoin && { rejoinFromEmployeeId: rejoinSelectedEmployeeId }),
      // Employee basic fields (only those expected by backend)
      departmentId: formData.departmentId,
      empCode: formData.empCode,
      designationId: formData.designationId,
      subDepartmentId: formData.subDepartmentId,
      gradeId: formData.gradeId,
      categoryId: formData.categoryId,
      reportingManagerId:
        totalEmployees > 0 ? formData.reportingManagerId : null,
      branchId: formData.branchId,
      employmentType: formData.employmentType,
      employmentStatus: isRejoin ? "Probation" : formData.employmentStatus,
      // PAN sentinels: PANNOTAVBL / PANAPPLIED; real PANs stored uppercase.
      panNumber: panForSave,
      aadharNumber: formData.aadharNumber, // Backend expects 'aadhaarNo'
      officialEmail: formData.email, // Backend expects 'officialEmail'
      // personalEmail: formData.personalEmail, // Backend expects 'personalEmail'
      joiningDate: formData.joiningDate, // Backend expects 'dateOfJoin'
      confirmationDate: isRejoin ? null : formData.confirmationDate,
      resignationDate: isRejoin ? null : formData.resignationDate,
      // For rejoin or Terminated status, send terminationDate and set relievedDate to null
      relievedDate: isRejoin || formData.employmentStatus === "Terminated" ? null : formData.relievedDate,
      terminationDate: isRejoin || formData.employmentStatus !== "Terminated" ? null : formData.terminationDate,
      reason: isRejoin ? null : formData.reason,
      essEnabled: formData.essEnabled !== undefined ? !!formData.essEnabled : false,
      inviteSent: formData.inviteSent !== undefined ? !!formData.inviteSent : false,
      ctc: formData.ctc,
      remarks: formData.remarks || null,
      // Statutory Compliance fields
      pfApplicable: formData.pfApplicable !== false, // Default to true
      ceilingPf: formData.ceilingPf !== false, // Default to true
      epsApplicable: formData.epsApplicable || false, // Default to false
      esiApplicable: formData.esiApplicable || false, // Default to false
      ptApplicable: formData.ptApplicable !== false, // Default to true
      gratuityApplicable: formData.gratuityApplicable !== false, // Default to true
      // User details (for user update) - using backend expected field names
      name: nameForSave, // Normalized (trim + collapse spaces), capitalization preserved e.g. "Kranthi Dev Sai"
      phone: formData.user?.phone, // Backend expects 'mobileNumber'
      dateOfBirth: formData.user?.dateOfBirth,
      gender: formData.user?.gender,
      bloodGroup: formData.user?.bloodGroup,
      personalEmail: formData.user?.email, // Backend expects 'email' for personal email
      forceAttendanceRecalculation: false,
      lockedAttendanceMonths: [],
    };

    // Call onSaveSection with error handling for unique constraints
    const wasNewEmployee = !employeeData.id;
    try {
      await onSaveSection(sectionData);

      // Show success toast message first
      if (employeeData.id) {
        toast.success("Employee updated successfully");
      } else {
        toast.success("Employee onboarded successfully");
      }

      try {
        onSaveSuccess?.();
      } catch (_e) {
        /* ignore parent callback errors */
      }

      // After successful save, clear confirmation flag and snapshot current masters
      const wasConfirmed = forceConfirmed || confirmedMasterChange;
      if (employeeData.id) {
        setInitialEmploymentDates({
          joiningDate: formData.joiningDate || null,
          relievedDate: formData.relievedDate || null,
        });
        setInitialIdentitySnapshot(currentIdentitySnapshot);
      }
      setLockedAttendanceWarningMonths([]);
      if (wasConfirmed) {
        setConfirmedMasterChange(false);
        setInitialMasterDetails({
          branchId: formData.branchId,
          designationId: formData.designationId,
          subDepartmentId: formData.subDepartmentId,
          departmentId: formData.departmentId,
          gradeId: formData.gradeId,
          categoryId: formData.categoryId,
          reason: formData.reason,
        });
        // Do NOT navigate further
        return { saved: true, created: wasNewEmployee }; // prevent onNext navigation
      }

      // Navigate to next tab after successful save (if not last tab)
      // Original functionality: form's handleSave calls onNext after successful save
      if (!isLastTab && onNext) {
        onNext();
      }
      return { saved: true, created: wasNewEmployee };
    } catch (error) {
      // Show error toast message
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An error occurred while saving employee details";
      toast.error(errorMessage);

      // Handle validation errors from backend (including Sequelize validation errors)
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        const newFormErrors = { ...formErrors };

        // Map backend error fields to frontend field names
        // Note: Backend already maps fields correctly based on model (Employee vs User)
        // Backend sends: 'email' for Employee.email (Official Email) and 'personalEmail' for User.email (Personal Email)
        Object.keys(backendErrors).forEach((field) => {
          // Backend already handles the mapping, so we can use the field name directly
          // But we still need to handle some edge cases and legacy field names
          let frontendField = field;
          
          // Field mapping from backend to frontend
          // Backend now correctly maps:
          // - Employee.email -> 'email' (Official Email)
          // - User.email -> 'personalEmail' (Personal Email)
          const fieldMapping = {
            // Email fields - backend already maps correctly, but keep for safety
            'email': 'email', // Backend sends 'email' for Employee.email (Official Email)
            'personalEmail': 'personalEmail', // Backend sends 'personalEmail' for User.email
            'officialEmail': 'email', // Legacy/fallback mapping
            'user.email': 'personalEmail', // Legacy/fallback mapping
            
            // Other fields
            'phone': 'mobileNumber',
            'mobileNumber': 'mobileNumber',
            'user.phone': 'mobileNumber',
            'name': 'name',
            'user.name': 'name',
            'dateOfBirth': 'dateOfBirth',
            'user.dateOfBirth': 'dateOfBirth',
            'gender': 'gender',
            'user.gender': 'gender',
            'bloodGroup': 'bloodGroup',
            'user.bloodGroup': 'bloodGroup',
            'panNumber': 'panNumber',
            'aadharNumber': 'aadharNumber',
            'aadhaarNumber': 'aadharNumber',
            'empCode': 'empCode',
            'joiningDate': 'joiningDate',
          };
          
          // Apply mapping if exists, otherwise use field as-is (backend already mapped it correctly)
          if (fieldMapping[field]) {
            frontendField = fieldMapping[field];
          }
          
          // Set the error for the mapped field
          newFormErrors[frontendField] = backendErrors[field];
          
          // Mark field as touched so error displays immediately
          setTouchedFields((prev) => ({ ...prev, [frontendField]: true }));
        });

        // Update form errors state
        setFormErrors(newFormErrors);
        
        // Scroll to first error field
        const firstErrorField = Object.keys(newFormErrors)[0];
        if (firstErrorField) {
          setTimeout(() => {
            const element = document.getElementById(firstErrorField) || 
                          document.querySelector(`[name="${firstErrorField}"]`) ||
                          document.querySelector(`input[id*="${firstErrorField}"]`);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
              element.focus();
            }
          }, 100);
        }
        
        // Re-throw so parent knows save failed and keeps popup open
        throw error;
      }
      
      // Re-throw error so parent can handle it if it's not a validation error
      throw error;
    }
  };

  // Master details confirmation handlers
  const handleMasterDetailsConfirm = () => {
    setShowMasterDetailsWarning(false);
    setConfirmedMasterChange(true);
    // Proceed with save by calling handleSave again (now bypasses warning)
    setTimeout(() => handleSave({ skipWarning: true, forceConfirmed: true }), 0);
  };

  const handleMasterDetailsCancel = () => {
    setShowMasterDetailsWarning(false);
    setConfirmedMasterChange(false);
  };

  const dismissLockedAttendanceDateModal = () => {
    setShowLockedAttendanceDateWarning(false);
    setLockedAttendanceWarningMonths([]);
  };

  // Expose handleSave to parent component via callback
  useEffect(() => {
    if (onSaveTrigger) {
      // Wrap in a function to ensure we always call the latest version
      onSaveTrigger(() => handleSave());
    }
  }, [onSaveTrigger, formData.inviteSent, employeeData.id]); // Re-expose when key data changes

  return (
    <div className="space-y-4" style={{ height: "60vh" }}>
      {/* Navigation Buttons - Top Right */}
      <div className="sticky top-0 z-5 flex items-center justify-end bg-white px-3 py-2 rounded-none shadow-sm">
        {/* Navigation Buttons - Hide when hideButtons is true (but keep in DOM for footer to trigger) */}
        <div className={`flex space-x-2 ${hideButtons ? "hidden" : ""}`}>
          {!isFirstTab && (
            <button
              onClick={onPrevious}
              className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded-none hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm"
              disabled={loading}
            >
              Previous
            </button>
          )}
          <button
            onClick={onCancel}
            className="px-3 py-1.5 bg-red-500 text-white rounded-none hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-sm"
            disabled={loading}
          >
            {isEmployeeView ? "Close" : "Cancel"}
          </button>
          {!isLastTab ? (
            <button
              onClick={handleSave}
              className={`px-3 py-1.5 rounded-none focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm ${
                !loading && (!readOnly || isEmployeeView)
                  ? "bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500"
                  : "bg-gray-400 text-gray-600 cursor-not-allowed"
              }`}
              disabled={loading || (readOnly && !isEmployeeView)}
            >
              {isEmployeeView
                ? "Complete your profile!"
                : employeeData.id
                ? "Update Employee"
                : "Onboard Employee"}
            </button>
          ) : (
            <button
              onClick={handleSave}
              className={`px-3 py-1.5 rounded-none focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm ${
                !loading && !readOnly
                  ? "bg-green-500 text-white hover:bg-green-600 focus:ring-green-500"
                  : "bg-gray-400 text-gray-600 cursor-not-allowed"
              }`}
              disabled={loading || readOnly}
            >
              {employeeData.id ? "Update Employee" : "Save"}
            </button>
          )}
        </div>
      </div>

      {/* Match Personal tab style: lock form fields when readOnly, without disabling the top action buttons */}
      <fieldset disabled={readOnly} className={readOnly ? "opacity-75" : ""}>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
        <div className="relative">
          <FloatingInput
            id="empCode"
            label="Employee Code"
            value={formData.empCode || ""}
            onChange={handleInputChange}
            onBlur={(e) => handleFieldBlur("empCode", e.target.value)}
            required
            error={formErrors.empCode}
            placeholder={
              employeeCodeStructure?.patternType === "automated"
                ? "Auto-generated"
                : "Letters, numbers, -, /, . (1-20 chars)"
            }
            maxLength={20}
            disabled={
              readOnly ||
              isEmployeeView ||
              employeeCodeStructure?.patternType === "automated"
            }
            readOnly={
              isEmployeeView ||
              employeeCodeStructure?.patternType === "automated"
            }
          />
          {employeeCodeStructure?.patternType === "automated" && (
            <div className="absolute right-3 top-3 flex items-center">
              {isGeneratingCode ? (
                <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              ) : (
                <span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-1 rounded-none border border-green-200">
                  Auto
                </span>
              )}
            </div>
          )}
          {/* {employeeCodeStructure?.patternType === 'automated' && !isGeneratingCode && (
                        <button
                            type="button"
                            onClick={generateAutomatedEmployeeCode}
                            className="absolute right-12 top-3 text-blue-600 hover:text-blue-800 text-xs p-1 rounded hover:bg-blue-50 transition-colors"
                            title="Regenerate code"
                        >
                            🔄
                        </button>
                    )} */}
        </div>
        <FloatingInput
          id="name"
          label="Name"
          value={formData.user?.name || ""}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const raw = e.target.value;
            const value = toTitleCaseName(raw);
            setEmployeeData((prev) => ({
              ...prev,
              user: { ...prev.user, name: value },
            }));
            handleFieldChangeWithValidation("name", value);
          }}
          onBlur={(e) => {
            const raw = e.target.value;
            handleFieldBlur("name", raw);
            // Normalize only whitespace (trim + collapse spaces), preserve capitalization e.g. "Kranthi Dev Sai"
            const normalized = normalizeNameInput(raw);
            if (normalized !== raw && !validateName(raw)) {
              setEmployeeData((prev) => ({
                ...prev,
                user: { ...prev.user, name: normalized },
              }));
              setFormErrors((prev) => ({ ...prev, name: null }));
            }
          }}
          required
          error={formErrors.name}
          placeholder="Name"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <SearchableSelect
          id="gender"
          label="Gender"
          value={formData.user?.gender}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            setEmployeeData((prev) => ({
              ...prev,
              user: { ...prev.user, gender: value },
            }));
            handleFieldChangeWithValidation("gender", value);
          }}
          onBlur={(e) => handleFieldBlur("gender", e.target.value)}
          staticOptions={[
            { value: "Male", label: "Male" },
            { value: "Female", label: "Female" },
            { value: "Other", label: "Other" },
          ]}
          required
          error={formErrors.gender}
          disabled={readOnly || isEmployeeView}
        />
        <div>
          <DatePicker
            id="dateOfBirth"
            label="Date of Birth"
            value={formData.user?.dateOfBirth || ""}
            min={
              new Date(new Date().setFullYear(new Date().getFullYear() - 100))
                .toISOString()
                .split("T")[0]
            }
            max={
              new Date(new Date().setFullYear(new Date().getFullYear() - 18))
                .toISOString()
                .split("T")[0]
            }
            disabled={readOnly || isEmployeeView}
            darkMode={!!darkMode}
            className="w-full"
            required
            error={formErrors.dateOfBirth}
            maxErrorText="Employee must be at least 18 years old"
            minErrorText="Employee age cannot be more than 100 years"
            onChange={(e) => {
              if (isEmployeeView) return;
              const value = e.target.value;
              setEmployeeData((prev) => ({
                ...prev,
                user: { ...prev.user, dateOfBirth: value },
              }));
              handleFieldChangeWithValidation("dateOfBirth", value);
            }}
          />
        </div>
        <SearchableSelect
          id="branchId"
          label="Branch"
          value={formData.branchId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("branchId", e.target.value)}
          staticOptions={masterData.branches.map((branch) => ({
            value: branch.id,
            label: branch.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.branchId}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="designationId"
          label="Designation"
          value={formData.designationId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("designationId", e.target.value)}
          staticOptions={masterData.designations.map((designation) => ({
            value: designation.id,
            label: designation.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.designationId}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="departmentId"
          label="Department"
          value={formData.departmentId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("departmentId", e.target.value)}
          staticOptions={masterData.departments.map((department) => ({
            value: department.id,
            label: department.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.departmentId}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="subDepartmentId"
          label="Sub Department"
          value={formData.subDepartmentId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("subDepartmentId", e.target.value)}
          staticOptions={masterData.subDepartments.map((subDept) => ({
            value: subDept.id,
            label: subDept.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.subDepartmentId}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="gradeId"
          label="Grade"
          value={formData.gradeId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("gradeId", e.target.value)}
          staticOptions={masterData.grades.map((grade) => ({
            value: grade.id,
            label: grade.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.gradeId}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="categoryId"
          label="Category"
          value={formData.categoryId}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("categoryId", e.target.value)}
          staticOptions={masterData.categories.map((category) => ({
            value: category.id,
            label: category.name,
          }))}
          darkMode={darkMode}
          required
          error={formErrors.categoryId}
          disabled={readOnly || isEmployeeView}
        />

        {/* Reporting Manager - Only show if there are existing employees */}
        {totalEmployees > 0 && (
          <SearchableSelect
            id="reportingManagerId"
            label="Reporting Manager"
            value={formData.reportingManagerId}
            onChange={handleInputChange}
            onBlur={(e) =>
              handleFieldBlur("reportingManagerId", e.target.value)
            }
            error={formErrors.reportingManagerId}
            fetchOptions={fetchReportingEmployees}
            darkMode={darkMode}
            isEmployee={true}
            required={!(formData.user?.role === "admin" || formData.user?.role === "super_admin")}
            disabled={readOnly || isEmployeeView}
          />
        )}
        <DatePicker
          id="joiningDate"
          label="Date of Join"
          value={formData.joiningDate || ""}
          min={(() => {
            if (formData.user?.dateOfBirth) {
              const d = new Date(formData.user.dateOfBirth);
              d.setFullYear(d.getFullYear() + 18);
              return d.toISOString().split("T")[0];
            }
            return "";
          })()}
          max={new Date().toISOString().split("T")[0]}
          disabled={readOnly || isEmployeeView}
          darkMode={!!darkMode}
          className="w-full"
          error={formErrors.joiningDate}
          minErrorText="Employee must be at least 18 years old at joining date"
          maxErrorText="Joining Date cannot be in the future"
          validateOnType={true}
          onChange={(e) => {
            if (isEmployeeView) return;
            const value = e.target.value;
            setEmployeeData((prev) => ({
              ...prev,
              joiningDate: value,
            }));
            handleFieldChangeWithValidation("joiningDate", value);
          }}
        />
        <SearchableSelect
          id="employmentType"
          label="Employee Type"
          value={formData.employmentType}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            handleInputChange({
              target: { name: "employmentType", value: value },
            });
            handleFieldChangeWithValidation("employmentType", value);
          }}
          onBlur={(e) => handleFieldBlur("employmentType", e.target.value)}
          staticOptions={[
            { value: "Full-time", label: "Full-Time" },
            { value: "External", label: "External" },
            // { value: "Part-time", label: "Part-Time" },
            // { value: "Contract", label: "Contract" },
            // { value: "Intern", label: "Intern" },
            // { value: "Consultant", label: "Consultant" },
          ]}
          required
          error={formErrors.employmentType}
          disabled={readOnly || isEmployeeView}
        />
        <SearchableSelect
          id="employmentStatus"
          label="Employment Status"
          value={formData.employmentStatus}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            if (
              formData.id &&
              initialEmploymentStatus === "Confirmed" &&
              value === "Probation"
            ) {
              setShowConfirmedToProbationWarning(true);
              return;
            }
            handleEmploymentStatusChange(value);
            // Also mark this field as touched and validate it
            handleFieldChangeWithValidation("employmentStatus", value);
          }}
          onBlur={(e) => handleFieldBlur("employmentStatus", e.target.value)}
          staticOptions={[
            { value: "Probation", label: "Probation" },
            { value: "Confirmed", label: "Confirmed" },
            { value: "Resigned", label: "Resigned" },
            { value: "Relieved", label: "Relieved" },
            { value: "Terminated", label: "Terminated" },
          ]}
          darkMode={darkMode}
          required
          error={formErrors.employmentStatus}
          disabled={readOnly || isEmployeeView}
        />
        {formData.employmentStatus === "Confirmed" && (
          <DatePicker
            id="confirmationDate"
            label="Confirmation Date"
            type={isEmployeeView ? "text" : "date"}
            value={formData.confirmationDate || ""}
            onChange={(e) => {
              if (isEmployeeView) return;
              const v = e.target.value;
              handleInputChange({
                target: { name: "confirmationDate", value: v },
              });
              handleFieldChangeWithValidation("confirmationDate", v);
            }}
            required
            error={formErrors.confirmationDate}
            min={formData.joiningDate || new Date().toISOString().split("T")[0]}
            // max={new Date().toISOString().split('T')[0]}
            disabled={readOnly || isEmployeeView}
            readOnly={isEmployeeView}
          />
        )}
        {(formData.employmentStatus === "Resigned" ||
          formData.employmentStatus === "Relieved") && (
          <>
            <DatePicker
              id="resignationDate"
              value={formData.resignationDate || ""}
              onChange={(e) => {
                if (isEmployeeView) return;
                handleInputChange({
                  target: { name: "resignationDate", value: e.target.value },
                });
                handleFieldChangeWithValidation(
                  "resignationDate",
                  e.target.value
                );
              }}
              label="Date of Resignation"
              type={isEmployeeView ? "text" : "date"}
              onBlur={(e) => handleFieldBlur("resignationDate", e.target.value)}
              required
              min={formData.joiningDate}
              minErrorText="Resignation Date cannot be before Joining Date"
              error={formErrors.resignationDate}
              validateOnType={false}
              disabled={readOnly || isEmployeeView}
            />
            <DatePicker
              id="relievedDate"
              value={formData.relievedDate || ""}
              onChange={(e) => {
                if (isEmployeeView) return;
                handleInputChange({
                  target: { name: "relievedDate", value: e.target.value },
                });
                handleFieldChangeWithValidation("relievedDate", e.target.value);
              }}
              onBlur={(e) => handleFieldBlur("relievedDate", e.target.value)}
              label="Date of Relieved"
              required
              min={formData.resignationDate || formData.joiningDate}
              minErrorText={
                formData.resignationDate
                  ? "Relieved Date cannot be before Resignation Date"
                  : "Relieved Date cannot be before Joining Date"
              }
              error={formErrors.relievedDate}
              validateOnType={false}
              disabled={readOnly || isEmployeeView}
            />
            <SearchableSelect
              id="reasonForResignation"
              label="Resignation Reason"
              value={formData.reason}
              onChange={(e) => {
                if (isEmployeeView) return; // Prevent changes for employee view
                const value = e.target.value;
                handleInputChange({ target: { name: "reason", value: value } });
                handleFieldChangeWithValidation("reason", value);
              }}
              onBlur={(e) => handleFieldBlur("reason", e.target.value)}
              staticOptions={masterData.resignationReasons.map((reason) => ({
                value: reason.id,
                label: reason.name,
              }))}
              darkMode={darkMode}
              required
              error={formErrors.reason}
              disabled={readOnly || isEmployeeView}
            />
          </>
        )}
        {formData.employmentStatus === "Terminated" && (
          <>
            <DatePicker
              value={formData.terminationDate || ""}
              onChange={(e) => {
                if (isEmployeeView) return;
                setEmployeeData((prev) => ({
                  ...prev,
                  terminationDate: e.target.value,
                }));
                handleFieldChangeWithValidation("terminationDate", e.target.value);
              }}
              label="Termination Date"
              required
              min={formData.joiningDate}
              max={new Date().toISOString().slice(0, 10)}
              minErrorText="Termination Date cannot be before Joining Date"
              maxErrorText="Termination Date cannot be in the future"
              error={formErrors.terminationDate}
              validateOnType={false}
              disabled={readOnly || isEmployeeView}
            />
            <SearchableSelect
              id="reasonForTermination"
              label="Termination Reason"
              value={formData.reason}
              onChange={(e) => {
                if (isEmployeeView) return; // Prevent changes for employee view
                const value = e.target.value;
                handleInputChange({ target: { name: "reason", value: value } });
                handleFieldChangeWithValidation("reason", value);
              }}
              onBlur={(e) => handleFieldBlur("reason", e.target.value)}
              staticOptions={masterData.terminationReasons.map((reason) => ({
                value: reason.id,
                label: reason.name,
              }))}
              darkMode={darkMode}
              required
              error={formErrors.reason}
              disabled={readOnly || isEmployeeView}
            />
          </>
        )}
        <FloatingInput
          id="mobileNumber"
          label="Mobile Number"
          value={formData.user?.phone}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            setEmployeeData((prev) => ({
              ...prev,
              user: { ...prev.user, phone: value },
            }));
            handleFieldChangeWithValidation("mobileNumber", value);
          }}
          onBlur={(e) => handleFieldBlur("mobileNumber", e.target.value)}
          required
          error={formErrors.mobileNumber}
          maxLength={10}
          placeholder="10-digit mobile number"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <FloatingInputWithNA
          id="personalEmail"
          label="Personal Email"
          type="email"
          value={formData.user?.email || ""}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            setEmployeeData((prev) => ({
              ...prev,
              user: { ...prev.user, email: value },
            }));
            handleFieldChangeWithValidation("personalEmail", value);
          }}
          onBlur={(e) => handleFieldBlur("personalEmail", e.target.value)}
          required
          error={formErrors.personalEmail}
          placeholder="Personal Email"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <FloatingInputWithNA
          id="email"
          label="Official Email"
          type="email"
          value={formData.email || ""}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("email", e.target.value)}
          required
          error={formErrors.email}
          placeholder="Official Email"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <SearchableSelect
          id="bloodGroup"
          label="Blood Group"
          value={formData.user?.bloodGroup}
          onChange={(e) => {
            if (isEmployeeView) return; // Prevent changes for employee view
            const value = e.target.value;
            setEmployeeData((prev) => ({
              ...prev,
              user: { ...prev.user, bloodGroup: value },
            }));
            handleFieldChangeWithValidation("bloodGroup", value);
          }}
          onBlur={(e) => handleFieldBlur("bloodGroup", e.target.value)}
          staticOptions={[
            { label: "A+", value: "A+" },
            { label: "A-", value: "A-" },
            { label: "B+", value: "B+" },
            { label: "B-", value: "B-" },
            { label: "AB+", value: "AB+" },
            { label: "AB-", value: "AB-" },
            { label: "O+", value: "O+" },
            { label: "O-", value: "O-" },
            { label: "NA", value: "NA" },
          ]}
          required
          error={formErrors.bloodGroup}
          disabled={readOnly || isEmployeeView}
        />
        <FloatingInput
          id="aadharNumber"
          label="Aadhaar No"
          value={formData.aadharNumber || ""}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("aadharNumber", e.target.value)}
          required
          error={formErrors.aadharNumber}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={12}
          placeholder="12-digit Aadhaar number"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <FloatingInputWithNA
          id="panNumber"
          label="PAN"
          value={formData.panNumber || ""}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("panNumber", e.target.value)}
          required
          error={formErrors.panNumber}
          maxLength={10}
          naOption={PAN_SENTINEL_NOT_AVAILABLE}
          extraOptions={[PAN_SENTINEL_APPLIED]}
          placeholder="PAN Number"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />
        <div className="flex flex-col">
          <FloatingInput
            id="ctc"
            label="CTC (Cost to Company)"
            type="number"
            value={formData.ctc || ""}
            onChange={handleInputChange}
            onBlur={(e) => handleFieldBlur("ctc", e.target.value)}
            required
            error={formErrors.ctc}
            min="0"
            step="0.01"
            placeholder="Enter CTC amount"
            disabled={readOnly || isEmployeeView}
            readOnly={isEmployeeView}
          />
          {/* CTC in words – always below the input box */}
          {formData.ctc && parseFloat(formData.ctc) > 0 && (
            <div className={`mt-1 text-xs ${darkMode ? "text-blue-300" : "text-blue-600"}`}>
              Rs. {numberToWords(formData.ctc)}
            </div>
          )}
        </div>
        <FloatingInput
          id="remarks"
          label="Remarks"
          value={formData.remarks || ""}
          onChange={handleInputChange}
          onBlur={(e) => handleFieldBlur("remarks", e.target.value)}
          placeholder="Remarks (Optional)"
          disabled={readOnly || isEmployeeView}
          readOnly={isEmployeeView}
        />

        {/* Statutory Compliance Section */}
        <div className="col-span-6 border-t border-gray-300 pt-2 mt-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center">
              <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Statutory Applicability
            </h3>
          </div>
          <div className="grid grid-cols-3 gap-x-3 gap-y-2">
            <div className="flex flex-col border border-gray-300 rounded px-2.5 py-2.5 hover:border-gray-400 transition-colors h-[3.3rem] justify-center">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="pfApplicable"
                  name="pfApplicable"
                  checked={formData.pfApplicable !== false}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      setPendingCheckboxChange({ name: 'pfApplicable', checked: false });
                      setShowStatutoryWarningModal({
                        show: true,
                        field: 'pfApplicable',
                        title: 'PF Applicability Warning',
                        message: 'All employees should mandatorily be applicable when establishment registered under EPFO.',
                      });
                    } else {
                      handleInputChange(e);
                    }
                  }}
                  disabled={readOnly || isEmployeeView}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                />
                <label
                  htmlFor="pfApplicable"
                  className="ml-2 text-sm font-medium text-gray-700 cursor-pointer flex-1"
                >
                  PF Applicable
                </label>
                <span className="px-2 py-0.5 text-xs font-semibold bg-blue-600 text-white rounded">
                  EPFO
                </span>
              </div>
            </div>

            <div className={`flex flex-col border rounded px-2.5 py-2.5 transition-colors h-[3.3rem] justify-center ${formData.pfApplicable === false ? 'border-gray-300 opacity-50' : 'border-gray-300 hover:border-gray-400'}`}>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ceilingPf"
                  name="ceilingPf"
                  checked={formData.pfApplicable === false ? true : (formData.ceilingPf !== false)}
                  onChange={(e) => {
                    // Only allow unchecking if PF is applicable
                    if (formData.pfApplicable !== false) {
                      if (!e.target.checked) {
                        setPendingCheckboxChange({ name: 'ceilingPf', checked: false });
                        setShowStatutoryWarningModal({
                          show: true,
                          field: 'ceilingPf',
                          title: 'Ceiling PF Information',
                          message: 'Unchecking this means PF deduction will be calculated on total BASIC + DA (without ₹15,000 ceiling limit).\n\nExample: If Basic + DA = ₹30,000, PF will be deducted on full ₹30,000 (₹3,600) instead of ₹15,000 (₹1,800).',
                        });
                      } else {
                        handleInputChange(e);
                      }
                    }
                  }}
                  disabled={readOnly || isEmployeeView || formData.pfApplicable === false}
                  className={`w-4 h-4 rounded border-gray-300 focus:ring-2 cursor-pointer ${formData.pfApplicable === false ? 'text-gray-400 cursor-not-allowed' : 'text-purple-600 focus:ring-purple-500'}`}
                />
                <label
                  htmlFor="ceilingPf"
                  className={`ml-2 text-sm font-medium cursor-pointer flex-1 ${formData.pfApplicable === false ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'}`}
                >
                  Ceiling PF
                </label>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded ${formData.pfApplicable === false ? 'bg-gray-400 text-gray-200' : 'bg-purple-600 text-white'}`}>
                  ₹15K
                </span>
              </div>
            </div>

            <div className="flex flex-col border border-gray-300 rounded px-2.5 py-2 hover:border-gray-400 transition-colors h-[3.3rem] justify-center relative">
              <div className="flex items-center gap-1.5 min-w-0">
                <input
                  type="checkbox"
                  id="epsApplicable"
                  name="epsApplicable"
                  checked={formData.epsApplicable || false}
                  onChange={handleInputChange}
                  disabled={readOnly || isEmployeeView}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-2 focus:ring-green-500 cursor-pointer flex-shrink-0"
                />
                <label
                  htmlFor="epsApplicable"
                  className="text-xs font-medium text-gray-700 cursor-pointer flex-shrink-0 whitespace-nowrap"
                >
                  EPS
                </label>
                {formData.epsApplicable && (
                  <>
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-green-600 text-white rounded flex-shrink-0 whitespace-nowrap" title="Auto-checked from Statutory Details">
                      Auto
                    </span>
                    <span className="text-[10px] text-green-600 italic truncate min-w-0 flex-1" title="Auto-checked from Statutory Details">
                      Auto-checked
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-col border border-gray-300 rounded px-2.5 py-2.5 hover:border-gray-400 transition-colors h-[3.3rem] justify-center">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="esiApplicable"
                  name="esiApplicable"
                  checked={formData.esiApplicable || false}
                  onChange={(e) => {
                    if (e.target.checked && !formData.esiApplicable) {
                      setPendingCheckboxChange({ name: 'esiApplicable', checked: true });
                      setShowStatutoryWarningModal({
                        show: true,
                        field: 'esiApplicable',
                        title: 'ESI Applicability Information',
                        message: 'ESI applicable only when the Basic+DA (Basic) is lesser than 21000 per month and subject to 50% of CTC/Gross salary as per the new code on social security Act 2020. Once they applicable, they can only come out of coverage in the contribution period April and October when the base salary is more than ceiling amount i.e 21000.',
                      });
                    } else {
                      handleInputChange(e);
                    }
                  }}
                  disabled={readOnly || isEmployeeView}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                />
                <label
                  htmlFor="esiApplicable"
                  className="ml-2 text-sm font-medium text-gray-700 cursor-pointer flex-1"
                >
                  ESI Applicable
                </label>
                <span className="px-2 py-0.5 text-xs font-semibold bg-indigo-600 text-white rounded">
                  ESI
                </span>
              </div>
            </div>

            <div className="flex flex-col border border-gray-300 rounded px-2.5 py-2.5 hover:border-gray-400 transition-colors h-[3.3rem] justify-center">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ptApplicable"
                  name="ptApplicable"
                  checked={formData.ptApplicable !== false}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      setPendingCheckboxChange({ name: 'ptApplicable', checked: false });
                      setShowStatutoryWarningModal({
                        show: true,
                        field: 'ptApplicable',
                        title: 'PT Applicability Warning',
                        message: 'Professional Tax is typically mandatory for employees earning above the threshold limit as per state regulations.',
                      });
                    } else {
                      handleInputChange(e);
                    }
                  }}
                  disabled={readOnly || isEmployeeView}
                  className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-2 focus:ring-orange-500 cursor-pointer"
                />
                <label
                  htmlFor="ptApplicable"
                  className="ml-2 text-sm font-medium text-gray-700 cursor-pointer flex-1"
                >
                  PT Applicable
                </label>
                <span className="px-2 py-0.5 text-xs font-semibold bg-orange-600 text-white rounded">
                  State
                </span>
              </div>
            </div>

            <div className="flex flex-col border border-gray-300 rounded px-2.5 py-2.5 hover:border-gray-400 transition-colors h-[3.3rem] justify-center">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="gratuityApplicable"
                  name="gratuityApplicable"
                  checked={formData.gratuityApplicable !== false}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      setPendingCheckboxChange({ name: 'gratuityApplicable', checked: false });
                      setShowStatutoryWarningModal({
                        show: true,
                        field: 'gratuityApplicable',
                        title: 'Gratuity Applicability Warning',
                        message: 'Gratuity is mandatory for employees who have completed 5 years of continuous service under the Payment of Gratuity Act, 1972.\n\nUnchecking this, Gratuity will not be part of CTC.',
                      });
                    } else {
                      handleInputChange(e);
                    }
                  }}
                  disabled={readOnly || isEmployeeView}
                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-2 focus:ring-teal-500 cursor-pointer"
                />
                <label
                  htmlFor="gratuityApplicable"
                  className="ml-2 text-sm font-medium text-gray-700 cursor-pointer flex-1"
                >
                  Gratuity Applicable
                </label>
                <span className="px-2 py-0.5 text-xs font-semibold bg-teal-600 text-white rounded">
                  5 Years
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      </fieldset>

      {/* Statutory Warning Modal */}
      {showStatutoryWarningModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {showStatutoryWarningModal.title}
              </h3>
            </div>
            <div className="px-6 py-4">
              <div className="text-sm text-gray-700 whitespace-pre-line">
                {showStatutoryWarningModal.message}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  // Cancel - don't uncheck, close modal
                  setShowStatutoryWarningModal({ show: false, field: null, message: '', title: '' });
                  setPendingCheckboxChange(null);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // OK - confirm the change
                  if (pendingCheckboxChange) {
                    // Special handling for PF Applicable: ensure Ceiling PF stays checked
                    if (pendingCheckboxChange.name === 'pfApplicable' && pendingCheckboxChange.checked === false) {
                      // Set PF to false and ensure Ceiling PF remains true (but disabled)
                      setEmployeeData((prev) => ({
                        ...prev,
                        pfApplicable: false,
                        ceilingPf: true,
                      }));
                      // Trigger validation
                      handleFieldChangeWithValidation('pfApplicable', false);
                      handleFieldChangeWithValidation('ceilingPf', true);
                    } else {
                      // Handle other checkbox changes (including ESI Applicable)
                      handleInputChange({
                        target: {
                          name: pendingCheckboxChange.name,
                          value: pendingCheckboxChange.checked,
                          type: 'checkbox',
                          checked: pendingCheckboxChange.checked,
                        },
                      });
                    }
                  }
                  setShowStatutoryWarningModal({ show: false, field: null, message: '', title: '' });
                  setPendingCheckboxChange(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {feedback && (
        <div
          className={`mt-2 ${
            feedback.type === "success" ? "text-green-600" : "text-red-600"
          }`}
        >
          {typeof feedback.message === "string"
            ? feedback.message
            : feedback.message?.error ||
              feedback.message?.name ||
              "Unexpected error"}
        </div>
      )}

      {/* Validation Summary - Only show when form is submitted or fields are touched */}
      {/* {Object.keys(formErrors).length > 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-none">
                    <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h3>
                    <ul className="text-sm text-red-700 space-y-1">
                        {Object.entries(formErrors).map(([field, error]) => (
                            <li key={field} className="flex items-start">
                                <span className="mr-2">•</span>
                                <span>{error}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )} */}

      {/* Confirmed → Probation blocked (same shell as Master Details warning) */}
      {showConfirmedToProbationWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div
            className={`rounded-lg p-6 max-w-md mx-4 shadow-lg ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirmed-to-probation-warning-title"
          >
            <div className="mb-4">
              <h3
                id="confirmed-to-probation-warning-title"
                className="text-lg font-semibold text-yellow-600 mb-2"
              >
                Employment Status Change
              </h3>
              <p className="text-sm">
                You are not allowed from confirmation to probation.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowConfirmedToProbationWarning(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowConfirmedToProbationWarning(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showRelievedLockedWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div
            className={`rounded-lg p-6 max-w-md mx-4 shadow-lg ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="relieved-locked-warning-title"
          >
            <div className="mb-4">
              <h3
                id="relieved-locked-warning-title"
                className="text-lg font-semibold text-yellow-600 mb-2"
              >
                Employment Status Change
              </h3>
              <p className="text-sm">
                This employee is in Relieved status and the date of relieved is
                on or before today. You cannot change employment status to any
                other value.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowRelievedLockedWarning(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowRelievedLockedWarning(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejoinBeforeRelieveWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div
            className={`rounded-lg p-6 max-w-md mx-4 shadow-lg ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rejoin-before-relieve-warning-title"
          >
            <div className="mb-4">
              <h3
                id="rejoin-before-relieve-warning-title"
                className="text-lg font-semibold text-yellow-600 mb-2"
              >
                Rejoin Not Allowed
              </h3>
              <p className="text-sm">
                Rejoin cannot be done before the selected employee&apos;s relieved date.
                Please wait until the relieved date is reached, then try again.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowRejoinBeforeRelieveWarning(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Master Details Warning Modal */}
      {showMasterDetailsWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`bg-white rounded-lg p-6 max-w-md mx-4 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-yellow-600 mb-2">Master Details Change Warning</h3>
              <p className="text-sm">
                You are editing master details. This means the masters details are applicable from date of joining and history cannot be retrieved for master details.
                {" "}
                If you wish to change master details we suggest to change from
                {" "}
                {onNavigateToTransfer ? (
                  <button
                    type="button"
                    className="underline text-blue-600 hover:text-blue-800 ml-1 mr-1"
                    onClick={() => {
                      setShowMasterDetailsWarning(false);
                      if (typeof onCancel === "function") {
                        onCancel();
                      }
                      onNavigateToTransfer();
                    }}
                  >
                    Actions → Transfer
                  </button>
                ) : (
                  <span className="font-semibold"> actions transfer </span>
                )}
                instead. Continue with caution.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleMasterDetailsCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMasterDetailsConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showLockedAttendanceDateWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div
            className={`rounded-lg p-6 max-w-lg mx-4 shadow-lg ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="locked-attendance-date-warning-title"
          >
            <div className="mb-4">
              <h3
                id="locked-attendance-date-warning-title"
                className="text-lg font-semibold text-amber-600 mb-2"
              >
                Unlock attendance first
              </h3>
              <p className="text-sm leading-relaxed">
                Changing Date of Join or Relieving Date would affect attendance
                in month(s) that are{" "}
                <span className="font-semibold">already locked</span>:{" "}
                {lockedAttendanceWarningMonths.length > 0
                  ? lockedAttendanceWarningMonths.map((m) => m.label).join(", ")
                  : "selected period"}
                . You cannot save this change until those month(s) are{" "}
                <span className="font-semibold">unlocked</span> from the
                Attendance screen. Revert the dates here or unlock attendance,
                then try again.
              </p>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={dismissLockedAttendanceDateModal}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
